import { Component } from '@theme/component';

/**
 * @typedef {Object} BrandMarqueeRefs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 * @property {HTMLElement} group
 * @property {HTMLButtonElement} toggle
 * @extends {Component<BrandMarqueeRefs>}
 */
class WeBrandMarquee extends Component {
  requiredRefs = ['viewport', 'track', 'group', 'toggle'];

  #lifecycle;
  #resizeObserver;
  #mobile = window.matchMedia('(max-width: 749px)');
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #userPaused = false;
  #focusPaused = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #cleanup() {
    this.#lifecycle?.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.querySelectorAll('[data-we-marquee-clone]').forEach(clone => clone.remove());
  }

  #initialize() {
    this.#cleanup();
    this.#lifecycle = new AbortController();
    const options = { signal: this.#lifecycle.signal };
    const { viewport, track, group } = this.refs;
    const clone = group.cloneNode(true);

    if (!(clone instanceof HTMLElement)) return;
    clone.removeAttribute('ref');
    clone.setAttribute('data-we-marquee-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.querySelectorAll('[id], [ref], [data-shopify-editor-block]').forEach(element => {
      element.removeAttribute('id');
      element.removeAttribute('ref');
      element.removeAttribute('data-shopify-editor-block');
    });
    clone.querySelectorAll('a').forEach(link => {
      const item = document.createElement('span');
      item.className = link.className;
      item.append(...link.childNodes);
      link.replaceWith(item);
    });
    clone.querySelectorAll('img').forEach(image => image.alt = '');
    track.append(clone);

    viewport.addEventListener('click', event => {
      if (!this.hasAttribute('data-enabled') || this.#userPaused || event.detail === 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.#userPaused = true;
      this.#sync();
    }, { ...options, capture: true });
    viewport.addEventListener('focusin', () => {
      this.#focusPaused = true;
      this.#sync();
    }, options);
    viewport.addEventListener('focusout', () => queueMicrotask(() => {
      if (!this.isConnected) return;
      this.#focusPaused = viewport.contains(document.activeElement);
      this.#sync();
    }), options);
    this.#mobile.addEventListener('change', () => this.#sync(), options);
    this.#reducedMotion.addEventListener('change', () => this.#sync(), options);
    document.addEventListener('visibilitychange', () => this.#sync(), options);
    window.addEventListener('resize', () => this.#sync(), { ...options, passive: true });
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#sync());
      this.#resizeObserver.observe(viewport);
    }
    this.#focusPaused = viewport.contains(document.activeElement);
    this.#sync();
  }

  #sync() {
    const { viewport, group, toggle } = this.refs;
    const count = group.children.length;
    const enabled = this.#mobile.matches && !this.#reducedMotion.matches && count > 1;
    const paused = this.#userPaused || this.#focusPaused || document.hidden;
    this.style.setProperty('--we-marquee-viewport', `${viewport.clientWidth}px`);
    this.style.setProperty('--we-marquee-duration', `${Math.max(20, count * 6)}s`);
    this.toggleAttribute('data-enabled', enabled);
    this.toggleAttribute('data-paused', paused);
    toggle.hidden = !enabled;
    toggle.setAttribute('aria-label', this.#userPaused ? toggle.dataset.resumeLabel : toggle.dataset.pauseLabel);
    toggle.querySelector('[data-we-marquee-pause]').hidden = this.#userPaused;
    toggle.querySelector('[data-we-marquee-play]').hidden = !this.#userPaused;
  }

  togglePlayback() {
    this.#userPaused = !this.#userPaused;
    this.#sync();
  }
}

if (!customElements.get('we-brand-marquee')) {
  customElements.define('we-brand-marquee', WeBrandMarquee);
}
